
import Vue from 'vue';
import BigNumber from 'bignumber.js';
import _clamp from "lodash/clamp";

export default Vue.extend({
	name: 'NumberWithIncrementer',
	props: {
		value: { type: Number, default: null },
		max: { type: Number, default: null },
		min: { type: Number, default: null },
		step: { type: Number, default: null },
		disabled: { type: Boolean, default: false },
		maxWidth: { type: String, default: null },
		padding: { type: String, default: '0 14px 0 0' },
		isCurrency: { type: Boolean, default: false },
		isPercent: { type: Boolean, default: false },
		condensed: { type: Boolean, default: false },
		numberMinWidth: { type: String, default: () => 'auto' },
		editableNumberInput: { type: Boolean, default: false },
		roundToNearestStepOnlyOnBlur: { type: Boolean, default: false },
		selectionStyles: { type: Object, default: () => ({}) },
	},
	data () {
		return {
			// localValue controls what’s shown in the text field.
			localValue: this.value !== null ? this.value.toString() : '',
			// Whether the caret is in the field. Only consulted under roundToNearestStepOnlyOnBlur.
			isFocused: false
		};
	},
	watch: {
		// Sync localValue with the parent's value if it changes externally — but never mid-entry.
		// The parent normalizes what we emit and echoes it straight back, so without this guard the
		// field would be rewritten under the caret on every keystroke.
		value (newVal) {
			if (this.deferringNormalization) return;
			this.localValue = newVal !== null ? newVal.toString() : '';
		}
	},
	computed: {
		computedMin (): number {
			return this.min !== null ? this.min : 0;
		},
		computedMax (): number {
			return this.max !== null && this.max > 0 ? this.max : Math.max(this.computedMin, 1);
		},
		isAtMax (): boolean {
			return this.computedMax > 1 && this.value > 0 && this.value === this.computedMax;
		},
		/*
		True while the user is actively typing in an opted-in field. Rounding to the step (or clamping)
		on every keystroke rewrites the digits as they are entered: on a step of 50, typing "5" after
		"1000" makes "10005", which rounds straight to "10000" — the character never appears. Callers
		that opt in get their entry left alone until blur, which is the one moment normalizing is not
		destructive.
		 */
		deferringNormalization (): boolean {
			return !!(this.roundToNearestStepOnlyOnBlur && this.isFocused);
		},
		computedMaxWidth (): string {
			if (this.maxWidth) return this.maxWidth;
			return this.isPercent ? '76px' : '60px';
		},
	},
	methods: {
		/**
		 * onInput is called on each keystroke.
		 * It strips non-numeric characters, clamps the value, updates localValue,
		 * forces the underlying input element to update, and emits the clamped value.
		 */
		onInput (newValue: string, isOnBlur: boolean): void {
			// Remove non-numeric characters but allow decimal points.
			let sanitized: string = newValue.replace(/[^0-9.]/g, '');

			// Handle multiple decimal points - keep only the first one
			const decimalIndex: number = sanitized.indexOf('.');
			if (decimalIndex !== -1) {
				sanitized = sanitized.slice(0, decimalIndex + 1) + sanitized.slice(decimalIndex + 1).replace(/\./g, '');
			}

			// If empty, default to computedMin; otherwise, try parseFloat first, then parseInt as fallback.
			let numeric: number;
			if (!sanitized) {
				numeric = this.computedMin;
			} else {
				// Try parseFloat first to handle decimal numbers
				numeric = parseFloat(sanitized);
				// If parseFloat fails (NaN), fall back to parseInt
				if (isNaN(numeric)) {
					numeric = parseInt(sanitized, 10);
					// If parseInt also fails, use computedMin
					if (isNaN(numeric)) {
						numeric = this.computedMin;
					}
				}
			}

			// Mid-entry: show exactly what was typed and let the parent clamp for its own state. The
			// min clamp has to wait too — typing "5" toward "50" would otherwise snap to the minimum.
			if (!isOnBlur && this.deferringNormalization) {
				this.localValue = sanitized;
				this.$emit('input', numeric);
				return;
			}

			// Round to the nearest step increment
			if (isOnBlur || !this.roundToNearestStepOnlyOnBlur) {
				numeric = this.roundToNearestStep(numeric);
			}

			numeric = _clamp(numeric, this.computedMin, this.computedMax);
			const clampedStr: string = numeric.toString();
			// Update localValue.
			this.localValue = clampedStr;

			// Force the underlying input element (via ref) to display the clamped value.
			this.$nextTick(() => {
				const inputEl = (this.$refs.numberInput as any)?.$el?.querySelector('input');
				if (inputEl && inputEl.value !== clampedStr) {
					inputEl.value = clampedStr;
				}
			});

			// Emit the clamped value so the parent component updates.
			this.$emit('input', numeric);
		},

		/**
		 * onBlur: When the field loses focus, ensure the value is clamped.
		 */
		onBlur (): void {
			// Clear the flag first so this pass rounds and clamps for real
			this.isFocused = false;
			this.onInput(this.localValue, true);
		},

		/**
		 * onFocus: When the field is focused, force the displayed value
		 * to be the clamped localValue.
		 */
		onFocus (): void {
			this.isFocused = true;
			this.$nextTick(() => {
				const inputEl = (this.$refs.numberInput as any)?.$el?.querySelector('input');
				if (inputEl && inputEl.value !== this.localValue) {
					inputEl.value = this.localValue;
				}
			});
		},

		/**
		 * onIncrement handles clicks on the increment/decrement buttons.
		 */
		onIncrement (isUp: boolean): void {
			const stepNum: BigNumber = new BigNumber(this.step && this.step > 0 ? this.step : 1);
			const currentVal: BigNumber = new BigNumber(this.value || 0);
			const newVal: BigNumber = isUp ? currentVal.plus(stepNum) : currentVal.minus(stepNum);

			if (newVal.isGreaterThan(this.computedMax) || newVal.isLessThan(this.computedMin)) {
				return;
			}

			const numeric: number = newVal.toNumber();
			this.$emit('input', numeric);
			this.localValue = numeric.toString();
		},

		roundToNearestStep (value: number): number {
			const stepValue: number = this.step && this.step > 0 ? this.step : 1;
			return new BigNumber(value)
				.div(stepValue)
				.integerValue(BigNumber.ROUND_HALF_UP)
				.times(stepValue)
				.toNumber();
		}
	}
});
